import { Link } from 'react-router';
import { SEO, BreadcrumbSchema, FAQSchema, ServiceSchema, SpeakableSchema } from '@/components/seo/seo';

function RatingBadge({ rating, size = 'md' }: { rating: 'red' | 'amber' | 'green'; size?: 'sm' | 'md' | 'lg' }) {
  const colors = {
    red: 'bg-rose-500 text-white',
    amber: 'bg-amber-500 text-white',
    green: 'bg-emerald-500 text-white',
  };
  const sizes = {
    sm: 'px-2 py-0.5 text-xs',
    md: 'px-3 py-1 text-sm',
    lg: 'px-4 py-2 text-base',
  };
  return (
    <span className={`inline-block font-bold rounded-full uppercase ${colors[rating]} ${sizes[size]}`}>
      {rating}
    </span>
  );
}

function QuoteCard({ quote, source }: { quote: string; source: string }) {
  return (
    <blockquote className="bg-surface-50 border-l-4 border-brand-500 p-6 rounded-r-xl">
      <p className="text-surface-800 italic">"{quote}"</p>
      <cite className="text-sm text-surface-600 mt-2 block not-italic">— {source}</cite>
    </blockquote>
  );
}

export function SomersetCouncilPage() {
  return (
    <div className="min-h-screen">
      <SEO
        title="Somerset Pothole Claims | GREEN Roads, RED Spend Scorecard | Fixtyer"
        description="Somerset's roads are rated GREEN — but the DfT has given the council a RED spend scorecard. B and C roads in RED condition nearly doubled from 2022 to 2024, and the council projects spending £1m less than its DfT allocation. What that means for your pothole claim."
        canonical="/councils/somerset"
      />
      <BreadcrumbSchema
        items={[
          { name: 'Home', url: '/' },
          { name: 'Councils', url: '/councils' },
          { name: 'Somerset', url: '/councils/somerset' },
        ]}
      />
      <FAQSchema
        faqs={[
          {
            question: "Somerset's road condition is rated GREEN — can I still claim?",
            answer: "Yes. The DfT's GREEN condition rating reflects network-wide averages, driven largely by A roads that have stayed in good shape. Section 58 turns on the specific defect that damaged your vehicle, not the average. Somerset's own data shows B and C roads in RED condition nearly doubled from 6% in 2022 to 11% in 2024, good-condition B/C roads fell from 67% to 59%, and the council still filled 24,483 potholes in 2024/25. Against a GREEN-rated council your claim needs strong, specific evidence — photos, measurements and prior reports — but the defects clearly exist.",
          },
          {
            question: "What does Somerset's RED spend scorecard mean for my claim?",
            answer: "The DfT has flagged Somerset as underinvesting relative to its funding allocation. The council's own transparency report projects £43.965m of capital spend for 2025/26 against a £44.965m DfT allocation — £1m less than it was given. Its March 2024 funding report also admits it is \"currently unable\" to supplement government grants with borrowing \"due to the financial emergency created by spiralling costs in adults and children's social care nationally.\" Your claim today still turns on the specific defect, but a council cutting investment while its B and C roads decline will find it progressively harder to show its maintenance system is reasonable.",
          },
          {
            question: 'What if my pothole was on a C road or an unclassified road?',
            answer: "Coverage thins out fast below A and B roads. Somerset surveys 50% of its C roads each year, so full coverage of the C network is only achieved every four years. Unclassified roads — 3,357.5km of the county's 6,721.4km network — are surveyed at 25% per year using Multi-Function Road Monitor vehicles, which the council notes record the same defects as SCANNER \"except road cracking.\" A defect on a minor rural road can sit between condition surveys for years, which makes routine safety inspections and prior public reports the records that decide your claim.",
          },
          {
            question: "Does Somerset's \"Most Potholed Road programme\" help my claim?",
            answer: "Potentially, yes. For 2025/26 the council says \"The top 10 roads in Somerset for reported potholes will be resurfaced.\" That is documented, ranked knowledge of exactly where its worst pothole hotspots are — built from report volumes. If your damage happened on a road with a heavy pothole-reporting history, that history supports an argument the council had actual or constructive notice of the road's condition before your incident.",
          },
          {
            question: 'Somerset filled 28,574 potholes in 2023/24 — what does that mean for claims?',
            answer: "The council's own report calls that figure \"Significantly above the usual trend for the highways network in Somerset\" and attributes it to the hot, dry summer of 2022 followed by a cold, wet winter with flooding and sub-zero spells. That is roughly 78 potholes filled every day for a year. A network producing defects that much faster than usual is exactly the environment where potholes form between scheduled inspections — and where photographs, prior reports and dated evidence of your specific defect carry the claim.",
          },
          {
            question: 'Who do I claim against for pothole damage in Somerset?',
            answer: "Somerset Council. It is the unitary highway authority for the county's entire 6,721.4km local road network, from the A roads linking Taunton, Bridgwater and Yeovil down to rural unclassified lanes. Your claim is made under Section 41 of the Highways Act 1980, which places a duty on the council to maintain the highway. Expect the council to defend under Section 58 by pointing to its inspection regime — which is why your evidence needs to address when the road was last inspected and what the council knew.",
          },
        ]}
      />
      <ServiceSchema 
        councilName="Somerset Council" 
        councilSlug="somerset" 
        rating="amber" 
      />
      <SpeakableSchema
        url="/councils/somerset"
        headline="Somerset Road Maintenance Report"
        cssSelectors={['[data-speakable="summary"]', 'h1', 'h2']}
      />

      {/* Hero */}
      <section className="relative bg-brand-950 text-white overflow-hidden">
        {/* Top chevrons */}
        <div 
          className="absolute top-0 left-0 right-0 h-8 z-10"
          style={{
            background: `repeating-linear-gradient(
              -45deg,
              #f97316,
              #f97316 40px,
              #1e40af 40px,
              #1e40af 80px
            )`
          }}
        />
        
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 pt-20 pb-16">
          <nav className="text-sm text-brand-400 mb-8 flex items-center gap-2">
            <Link to="/" className="hover:text-white transition-colors">Home</Link>
            <span>/</span>
            <Link to="/councils" className="hover:text-white transition-colors">Councils</Link>
            <span>/</span>
            <span className="text-white">Somerset</span>
          </nav>

          <div className="flex flex-wrap items-center gap-3 mb-4">
            <RatingBadge rating="amber" size="lg" />
            <span className="text-brand-300">Overall</span>
            <span className="text-brand-500">|</span>
            <span className="flex items-center gap-2">
              <RatingBadge rating="green" size="sm" /> <span className="text-brand-300 text-sm">Condition</span>
            </span>
            <span className="flex items-center gap-2">
              <RatingBadge rating="red" size="sm" /> <span className="text-brand-300 text-sm">Spend</span>
            </span>
            <span className="flex items-center gap-2">
              <RatingBadge rating="green" size="sm" /> <span className="text-brand-300 text-sm">Best Practice</span>
            </span>
          </div>
          
          <h1 className="font-display text-4xl sm:text-5xl font-bold leading-tight mb-6">
            Somerset: Good Roads Today, A RED Flag On The Money
          </h1>
          
          <p className="text-xl text-brand-200 max-w-2xl mb-8 leading-relaxed" data-speakable="summary">
            Somerset's road condition is still rated <strong className="text-emerald-400">GREEN</strong> and 
            its asset management earns GREEN for best practice. But the DfT has given the council a 
            <strong className="text-rose-400"> RED spend scorecard</strong> — it projects spending £1m less 
            than its 2025/26 allocation, admits it can no longer borrow to top up the budget, and its B and C 
            roads in RED condition have <strong className="text-rose-400">nearly doubled since 2022</strong>.
          </p>

          {/* Key stat callout */}
          <div className="bg-rose-500/20 border border-rose-500/30 rounded-2xl p-6 max-w-md">
            <div className="text-6xl font-bold text-rose-400">£1,000,000</div>
            <div className="text-white font-medium mt-2">Projected to go unspent in 2025/26</div>
            <div className="text-brand-300 text-sm mt-1">
              Somerset's own transparency report projects £43.965m of capital spend against a 
              £44.965m DfT allocation — on a 6,721.4km network that filled 24,483 potholes last year.
            </div>
          </div>
        </div>

        {/* Bottom chevrons */}
        <div 
          className="absolute bottom-0 left-0 right-0 h-8 z-10"
          style={{
            background: `repeating-linear-gradient(
              -45deg,
              #1e40af,
              #1e40af 40px,
              #f97316 40px,
              #f97316 80px
            )`
          }}
        />
      </section>

      {/* The RED Spend Scorecard */}
      <section className="py-16 bg-white">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="font-display text-3xl font-bold text-brand-950 mb-4">
            Why The DfT Gave Somerset A RED Spend Scorecard
          </h2>
          <p className="text-surface-600 mb-8">
            Six years of the council's own spending figures — from comfortably over allocation 
            to projecting £1m under it
          </p>

          <div className="bg-white rounded-2xl border border-surface-200 overflow-hidden mb-8">
            <table className="w-full">
              <thead>
                <tr className="bg-surface-100 border-b border-surface-200">
                  <th className="px-6 py-3 text-left text-sm font-semibold text-brand-900">Year</th>
                  <th className="px-6 py-3 text-center text-sm font-semibold text-brand-900">DfT capital allocation</th>
                  <th className="px-6 py-3 text-center text-sm font-semibold text-brand-900">Capital spend</th>
                  <th className="px-6 py-3 text-center text-sm font-semibold text-brand-900">Spend vs allocation</th>
                </tr>
              </thead>
              <tbody className="text-sm">
                <tr className="border-b border-surface-100">
                  <td className="px-6 py-4 font-mono text-brand-900">2020/21</td>
                  <td className="px-6 py-4 text-center">£37.291m</td>
                  <td className="px-6 py-4 text-center">£42.150m</td>
                  <td className="px-6 py-4 text-center font-medium text-emerald-600">+£4.86m over</td>
                </tr>
                <tr className="border-b border-surface-100">
                  <td className="px-6 py-4 font-mono text-brand-900">2021/22</td>
                  <td className="px-6 py-4 text-center">£28.110m</td>
                  <td className="px-6 py-4 text-center">£32.333m</td>
                  <td className="px-6 py-4 text-center font-medium text-emerald-600">+£4.22m over</td>
                </tr>
                <tr className="border-b border-surface-100">
                  <td className="px-6 py-4 font-mono text-brand-900">2022/23</td>
                  <td className="px-6 py-4 text-center">£28.110m</td>
                  <td className="px-6 py-4 text-center">£30.866m</td>
                  <td className="px-6 py-4 text-center font-medium text-emerald-600">+£2.76m over</td>
                </tr>
                <tr className="border-b border-surface-100">
                  <td className="px-6 py-4 font-mono text-brand-900">2023/24</td>
                  <td className="px-6 py-4 text-center">£31.341m</td>
                  <td className="px-6 py-4 text-center">£39.876m</td>
                  <td className="px-6 py-4 text-center font-medium text-emerald-600">+£8.54m over</td>
                </tr>
                <tr className="border-b border-surface-100">
                  <td className="px-6 py-4 font-mono text-brand-900">2024/25</td>
                  <td className="px-6 py-4 text-center">£35.929m</td>
                  <td className="px-6 py-4 text-center">£35.929m</td>
                  <td className="px-6 py-4 text-center font-medium text-surface-500">£0 — exactly matched</td>
                </tr>
                <tr className="bg-rose-50">
                  <td className="px-6 py-4 font-mono font-bold text-brand-900">2025/26 (projected)</td>
                  <td className="px-6 py-4 text-center font-bold">£44.965m</td>
                  <td className="px-6 py-4 text-center font-bold">£43.965m</td>
                  <td className="px-6 py-4 text-center font-bold text-rose-600">−£1.00m under</td>
                </tr>
              </tbody>
            </table>
          </div>

          <div className="grid md:grid-cols-2 gap-6 mb-8">
            <div className="bg-surface-50 rounded-xl p-6 border border-surface-200">
              <h3 className="font-semibold text-brand-900 mb-3">The Direction Of Travel</h3>
              <p className="text-surface-600 text-sm mb-3">
                For years Somerset spent comfortably more than the DfT gave it — £4.86m over in 2020/21, 
                £8.54m over in 2023/24. Then in 2024/25 spend landed at exactly the allocation, to the pound. 
                For 2025/26 the council projects spending <strong>£1m less than it has been allocated</strong>.
              </p>
              <p className="text-surface-600 text-sm">
                The DfT's spend scorecard flags exactly this: a council underinvesting relative to its 
                allocation. Somerset is the rare authority with GREEN roads and a RED chequebook.
              </p>
            </div>

            <div className="bg-rose-50 border border-rose-200 rounded-xl p-6">
              <h3 className="font-semibold text-rose-900 mb-3">The Borrowing Admission</h3>
              <p className="text-rose-800 text-sm mb-3">
                The historic overspends were funded partly by the council borrowing to top up government 
                grants. Its March 2024 funding report states that option has now gone — in its own words, 
                the council is "currently unable to do this due to the financial emergency".
              </p>
              <p className="text-rose-800 text-sm">
                A maintenance budget that used to be allocation-plus-borrowing is now allocation-minus-£1m. 
                That is the spending squeeze behind the RED scorecard.
              </p>
            </div>
          </div>

          <QuoteCard 
            quote="The Council has from time to time supplemented Government grants with additional borrowing as shown in the table but is currently unable to do this due to the financial emergency created by spiralling costs in adults and children's social care nationally."
            source="Network North Additional Highway Maintenance Funding, Somerset Council, March 2024"
          />

          <div className="bg-brand-950 text-white rounded-2xl p-8 mt-8">
            <h3 className="font-display text-xl font-bold mb-4">Why Underspend Undermines Section 58</h3>
            <p className="text-brand-200 mb-4">
              Section 58 of the Highways Act 1980 lets a council escape liability if it took such care as was 
              reasonably required to keep the road safe. The statute expressly directs courts to consider the 
              standard of maintenance appropriate for the road and the state of repair a reasonable person 
              would expect.
            </p>
            <ul className="text-brand-300 space-y-2 mb-6">
              <li>• A council that does not invest even its full DfT allocation invites the question: was the maintenance system constrained by budget rather than engineering judgement?</li>
              <li>• Somerset's own report says some poor-condition roads are deliberately deprioritised in favour of preserving better ones — defensible asset management, but an admission that known-poor roads wait.</li>
              <li>• B and C roads are already deteriorating. Squeezed spend means the GREEN condition rating is a snapshot, not a trend.</li>
            </ul>
            <p className="text-white font-medium">
              Somerset's roads are good today. The DfT's RED flag says the money to keep them that way 
              isn't being spent — and every year that continues, the council's "reasonable system" story gets harder to tell.
            </p>
          </div>
        </div>
      </section>

      {/* The Condition Picture */}
      <section className="py-16 bg-surface-50">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="font-display text-3xl font-bold text-brand-950 mb-4">
            What The Condition Data Shows
          </h2>
          <p className="text-surface-600 mb-8">
            Five years of survey data from Somerset's own transparency report — A roads holding, 
            B and C roads sliding
          </p>

          {/* A-roads */}
          <div className="bg-white border border-surface-200 rounded-2xl p-6 mb-6">
            <h3 className="font-semibold text-brand-900 mb-4">A roads (681.5km): holding steady — this is where the GREEN rating comes from</h3>
            <div className="grid grid-cols-3 gap-4 mb-4">
              <div className="text-center">
                <div className="text-3xl font-bold text-rose-600">3%</div>
                <div className="text-sm text-rose-800">RED (2024)</div>
                <div className="text-xs text-surface-500">up from 2% in 2020-2023</div>
              </div>
              <div className="text-center">
                <div className="text-3xl font-bold text-amber-600">24%</div>
                <div className="text-sm text-amber-800">Amber</div>
                <div className="text-xs text-surface-500">up from 22% in 2020</div>
              </div>
              <div className="text-center">
                <div className="text-3xl font-bold text-emerald-600">73%</div>
                <div className="text-sm text-emerald-800">Green</div>
                <div className="text-xs text-surface-500">down from 76% in 2020</div>
              </div>
            </div>
            <p className="text-surface-600 text-sm">
              Honest assessment: Somerset's A roads are genuinely in decent shape, and the council describes 
              itself — fairly — as "a relatively well performing highways authority" against regional and 
              national peers. But A roads are just 681.5km of a 6,721.4km network.
            </p>
          </div>

          {/* B/C roads */}
          <div className="bg-amber-50 border border-amber-200 rounded-2xl p-6 mb-6">
            <h3 className="font-semibold text-amber-900 mb-4">B and C roads (2,682.4km — 40% of the network): deteriorating</h3>
            <div className="bg-white rounded-xl overflow-hidden border border-amber-200 mb-4">
              <table className="w-full text-sm">
                <thead>
                  <tr className="bg-amber-100 border-b border-amber-200">
                    <th className="px-4 py-2 text-left font-semibold text-amber-900">Year</th>
                    <th className="px-4 py-2 text-center font-semibold text-rose-700">Red</th>
                    <th className="px-4 py-2 text-center font-semibold text-amber-700">Amber</th>
                    <th className="px-4 py-2 text-center font-semibold text-emerald-700">Green</th>
                  </tr>
                </thead>
                <tbody>
                  <tr className="border-b border-amber-100">
                    <td className="px-4 py-2 font-mono">2020</td>
                    <td className="px-4 py-2 text-center">8%</td>
                    <td className="px-4 py-2 text-center">28%</td>
                    <td className="px-4 py-2 text-center">64%</td>
                  </tr>
                  <tr className="border-b border-amber-100">
                    <td className="px-4 py-2 font-mono">2021</td>
                    <td className="px-4 py-2 text-center">6%</td>
                    <td className="px-4 py-2 text-center">27%</td>
                    <td className="px-4 py-2 text-center">67%</td>
                  </tr>
                  <tr className="border-b border-amber-100">
                    <td className="px-4 py-2 font-mono">2022</td>
                    <td className="px-4 py-2 text-center">6%</td>
                    <td className="px-4 py-2 text-center">27%</td>
                    <td className="px-4 py-2 text-center">67%</td>
                  </tr>
                  <tr className="border-b border-amber-100">
                    <td className="px-4 py-2 font-mono">2023</td>
                    <td className="px-4 py-2 text-center">8%</td>
                    <td className="px-4 py-2 text-center">28%</td>
                    <td className="px-4 py-2 text-center">64%</td>
                  </tr>
                  <tr className="bg-amber-50">
                    <td className="px-4 py-2 font-mono font-bold">2024</td>
                    <td className="px-4 py-2 text-center font-bold text-rose-600">11%</td>
                    <td className="px-4 py-2 text-center font-bold">30%</td>
                    <td className="px-4 py-2 text-center font-bold">59%</td>
                  </tr>
                </tbody>
              </table>
            </div>
            <p className="text-amber-800 font-medium">
              B and C roads in RED condition nearly doubled in two years — 6% in 2022 to 11% in 2024 — 
              while good-condition roads fell from 67% to 59%. That is roughly 295km of B and C carriageway 
              that the council's own surveys say "should be considered for maintenance".
            </p>
          </div>

          {/* U-roads */}
          <div className="bg-white border border-surface-200 rounded-2xl p-6 mb-8">
            <h3 className="font-semibold text-brand-900 mb-4">Unclassified roads (3,357.5km — half the network): creeping up</h3>
            <div className="grid grid-cols-5 gap-2 mb-4">
              <div className="text-center">
                <div className="text-2xl font-bold text-surface-700">6%</div>
                <div className="text-xs text-surface-500">RED 2020</div>
              </div>
              <div className="text-center">
                <div className="text-2xl font-bold text-surface-700">6%</div>
                <div className="text-xs text-surface-500">RED 2021</div>
              </div>
              <div className="text-center">
                <div className="text-2xl font-bold text-surface-700">6%</div>
                <div className="text-xs text-surface-500">RED 2022</div>
              </div>
              <div className="text-center">
                <div className="text-2xl font-bold text-surface-700">6%</div>
                <div className="text-xs text-surface-500">RED 2023</div>
              </div>
              <div className="text-center">
                <div className="text-2xl font-bold text-rose-600">7%</div>
                <div className="text-xs text-rose-600 font-medium">RED 2024</div>
              </div>
            </div>
            <p className="text-surface-600 text-sm">
              7% RED across 3,357.5km is roughly 235km of unclassified rural lanes and estate roads flagged 
              for maintenance — measured by surveys that only cover a quarter of the U network each year.
            </p>
          </div>

          <div className="space-y-6">
            <QuoteCard 
              quote="This is reflected in the higher-than-normal incidences of potholes in 2023/24, peaking at 28,574. Significantly above the usual trend for the highways network in Somerset."
              source="Somerset Council Local Highways Maintenance Transparency Report, June 2025"
            />
            <QuoteCard 
              quote="This can likely be attributed to the dry and hot summer in 2022 followed by a cold and wet winter, with flooding events followed by extended below 0C periods. The variation in ground water levels has likely caused greater than usual flex in the carriageway across the year; opening cracks over the summer, which have allowed water ingress over the cold and wet periods of winter."
              source="Somerset Council Local Highways Maintenance Transparency Report, June 2025"
            />
          </div>
        </div>
      </section>

      {/* The Survey Cycle */}
      <section className="py-16 bg-white">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="font-display text-3xl font-bold text-brand-950 mb-4">
            The Four-Year Survey Cycle
          </h2>
          <p className="text-surface-600 mb-8">
            How often Somerset actually measures the condition of each road class — by its own account
          </p>

          <div className="bg-white rounded-2xl border border-surface-200 overflow-hidden mb-8">
            <table className="w-full">
              <thead>
                <tr className="bg-surface-100 border-b border-surface-200">
                  <th className="px-6 py-3 text-left text-sm font-semibold text-brand-900">Road class</th>
                  <th className="px-6 py-3 text-center text-sm font-semibold text-brand-900">Length</th>
                  <th className="px-6 py-3 text-center text-sm font-semibold text-brand-900">Surveyed per year</th>
                  <th className="px-6 py-3 text-center text-sm font-semibold text-brand-900">Full coverage takes</th>
                </tr>
              </thead>
              <tbody className="text-sm">
                <tr className="border-b border-surface-100">
                  <td className="px-6 py-4 font-medium text-brand-900">A roads</td>
                  <td className="px-6 py-4 text-center">681.5km</td>
                  <td className="px-6 py-4 text-center">100% (one direction)</td>
                  <td className="px-6 py-4 text-center">2 years</td>
                </tr>
                <tr className="border-b border-surface-100">
                  <td className="px-6 py-4 font-medium text-brand-900">B roads</td>
                  <td className="px-6 py-4 text-center" rowSpan={2}>2,682.4km (B+C)</td>
                  <td className="px-6 py-4 text-center">100% (one direction)</td>
                  <td className="px-6 py-4 text-center">2 years</td>
                </tr>
                <tr className="border-b border-surface-100 bg-amber-50">
                  <td className="px-6 py-4 font-medium text-brand-900">C roads</td>
                  <td className="px-6 py-4 text-center font-bold">50% (one direction)</td>
                  <td className="px-6 py-4 text-center font-bold text-amber-700">4 years</td>
                </tr>
                <tr className="bg-rose-50">
                  <td className="px-6 py-4 font-medium text-brand-900">Unclassified roads</td>
                  <td className="px-6 py-4 text-center font-bold">3,357.5km</td>
                  <td className="px-6 py-4 text-center font-bold">25%</td>
                  <td className="px-6 py-4 text-center font-bold text-rose-600">4 years</td>
                </tr>
              </tbody>
            </table>
          </div>

          <div className="grid md:grid-cols-2 gap-6 mb-8">
            <div className="bg-surface-50 rounded-xl p-6 border border-surface-200">
              <h3 className="font-semibold text-brand-900 mb-3">Half The Network, A Quarter At A Time</h3>
              <p className="text-surface-600 text-sm mb-3">
                Somerset's unclassified roads — 3,357.5km of rural lanes and estate roads, half the entire 
                network — are condition-surveyed at 25% per year. The C network is on a four-year cycle too. 
                A defect on a minor road can develop, worsen and damage vehicles years before the next 
                condition survey passes.
              </p>
              <p className="text-surface-600 text-sm">
                The council describes maintaining the rural unclassified network as "a significant challenge" 
                — its words, in its own report.
              </p>
            </div>

            <div className="bg-amber-50 border border-amber-200 rounded-xl p-6">
              <h3 className="font-semibold text-amber-900 mb-3">The Cracking Blind Spot</h3>
              <p className="text-amber-800 text-sm mb-3">
                Unclassified roads are surveyed with Multi-Function Road Monitor (MRM) vehicles, which the 
                report says "record the same defects except road cracking." Cracking is precisely how potholes 
                start — water enters through cracks, freezes, and breaks the surface apart.
              </p>
              <p className="text-amber-800 text-sm">
                So on half the network, the condition survey cannot see the leading indicator of the defect 
                most likely to damage your vehicle.
              </p>
            </div>
          </div>

          <div className="bg-brand-950 text-white rounded-2xl p-8">
            <h3 className="font-display text-xl font-bold mb-4">Why This Matters For Section 58</h3>
            <p className="text-brand-200 mb-4">
              To make out a Section 58 defence, Somerset must show it had a reasonable system for knowing 
              and acting on the condition of the road that damaged your vehicle. On minor roads, ask:
            </p>
            <ul className="text-brand-300 space-y-2 mb-6">
              <li>• When was your road last condition-surveyed — this year, or three years ago?</li>
              <li>• If U-road surveys cannot record cracking, how did the council track early deterioration on your road?</li>
              <li>• What does the Highway Safety Inspection Manual say the inspection frequency for your road should be — and was it met?</li>
              <li>• Had the defect been reported before, by anyone, through the council's "Report a problem on the road" service?</li>
            </ul>
            <p className="text-white font-medium">
              A GREEN network rating built on four-year survey cycles says little about the specific lane 
              that bent your wheel. Section 58 is fought defect by defect, record by record.
            </p>
          </div>
        </div>
      </section>

      {/* The Pothole Conveyor Belt */}
      <section className="py-16 bg-surface-50">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="font-display text-3xl font-bold text-brand-950 mb-4">
            112,648 Potholes In Five Years
          </h2>
          <p className="text-surface-600 mb-8">
            Somerset's own estimates of potholes filled — around 62 every day, every year
          </p>

          <div className="bg-white rounded-2xl border border-surface-200 overflow-hidden mb-8">
            <table className="w-full">
              <thead>
                <tr className="bg-surface-100 border-b border-surface-200">
                  <th className="px-6 py-3 text-left text-sm font-semibold text-brand-900">Year</th>
                  <th className="px-6 py-3 text-center text-sm font-semibold text-brand-900">Potholes filled (council estimate)</th>
                  <th className="px-6 py-3 text-center text-sm font-semibold text-brand-900">Per day</th>
                </tr>
              </thead>
              <tbody className="text-sm">
                <tr className="border-b border-surface-100">
                  <td className="px-6 py-4 font-mono text-brand-900">2020/21</td>
                  <td className="px-6 py-4 text-center">19,282</td>
                  <td className="px-6 py-4 text-center">~53</td>
                </tr>
                <tr className="border-b border-surface-100">
                  <td className="px-6 py-4 font-mono text-brand-900">2021/22</td>
                  <td className="px-6 py-4 text-center">19,078</td>
                  <td className="px-6 py-4 text-center">~52</td>
                </tr>
                <tr className="border-b border-surface-100">
                  <td className="px-6 py-4 font-mono text-brand-900">2022/23</td>
                  <td className="px-6 py-4 text-center">21,231</td>
                  <td className="px-6 py-4 text-center">~58</td>
                </tr>
                <tr className="border-b border-surface-100 bg-rose-50">
                  <td className="px-6 py-4 font-mono text-brand-900">2023/24</td>
                  <td className="px-6 py-4 text-center font-bold text-rose-600">28,574</td>
                  <td className="px-6 py-4 text-center font-bold text-rose-600">~78</td>
                </tr>
                <tr className="border-b border-surface-100">
                  <td className="px-6 py-4 font-mono text-brand-900">2024/25</td>
                  <td className="px-6 py-4 text-center">24,483</td>
                  <td className="px-6 py-4 text-center">~67</td>
                </tr>
                <tr className="bg-brand-50">
                  <td className="px-6 py-4 font-bold text-brand-900">Five-year total</td>
                  <td className="px-6 py-4 text-center font-bold text-rose-600">112,648</td>
                  <td className="px-6 py-4 text-center font-bold">~62</td>
                </tr>
              </tbody>
            </table>
          </div>

          <div className="grid md:grid-cols-2 gap-6">
            <div className="bg-white rounded-xl p-6 border border-surface-200">
              <h3 className="font-semibold text-brand-900 mb-3">Still Elevated, Not Back To Normal</h3>
              <p className="text-surface-600 text-sm mb-3">
                The 2023/24 spike was the council's worst pothole year on record in this report — 
                "Significantly above the usual trend", in its own words. But 2024/25 didn't return to baseline: 
                24,483 potholes is still well above the pre-spike norm of roughly 19,000-21,000 a year.
              </p>
              <p className="text-surface-600 text-sm">
                For 2025/26 the council anticipates filling "approximately 23,000 potholes" — planning for 
                another elevated year, while projecting to spend £1m under its allocation.
              </p>
            </div>
            <div className="bg-amber-50 border border-amber-200 rounded-xl p-6">
              <h3 className="font-semibold text-amber-900 mb-3">The Top-10 Hotspot List</h3>
              <p className="text-amber-800 text-sm mb-3">
                Buried in the 2025/26 plans is the "Most Potholed Road programme": "The top 10 roads in 
                Somerset for reported potholes will be resurfaced."
              </p>
              <p className="text-amber-800 text-sm">
                That sentence confirms the council ranks its roads by reported pothole volume. If your incident 
                happened on a road with a heavy reporting history, that ranking data is evidence the council 
                knew — and a strong foundation for a notice argument.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* The Asset Management Admission */}
      <section className="py-16 bg-white">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="font-display text-3xl font-bold text-brand-950 mb-4">
            The Prevention-First Admission
          </h2>
          <p className="text-surface-600 mb-8">
            Somerset's strategy deliberately leaves some poor roads waiting — the report says so in plain terms
          </p>

          <div className="space-y-6 mb-8">
            <QuoteCard 
              quote="It should be noted that the application of asset management principles could mean that some roads in poor condition are a lower order of priority for maintenance than a road in better condition; prevention is a more cost-effective approach than addressing the symptoms through reactive maintenance."
              source="Somerset Council Local Highways Maintenance Transparency Report, June 2025"
            />
          </div>

          <div className="grid md:grid-cols-2 gap-6">
            <div className="bg-surface-50 rounded-xl p-6 border border-surface-200">
              <h3 className="font-semibold text-brand-900 mb-3">What This Admission Means</h3>
              <p className="text-surface-600 text-sm mb-3">
                Preventative-first asset management is genuinely good engineering — sealing a sound road is 
                cheaper than rebuilding a failed one, and Somerset plans to put 89% of its 2025/26 budget into 
                prevention. But the council openly states the consequence: some roads it knows to be in poor 
                condition are deliberately left lower in the queue.
              </p>
              <p className="text-surface-600 text-sm">
                If your pothole was on one of those deprioritised roads, the council's defence cannot be 
                "we didn't know" — its own strategy documents the choice to wait.
              </p>
            </div>
            <div className="bg-amber-50 border border-amber-200 rounded-xl p-6">
              <h3 className="font-semibold text-amber-900 mb-3">Questions Worth Asking</h3>
              <ul className="text-amber-800 text-sm space-y-2">
                <li>• Was your road in the RED or AMBER category at its last survey?</li>
                <li>• If so, was it scheduled for treatment — or deprioritised in favour of preserving better roads?</li>
                <li>• What interim safety measures applied to it while it waited?</li>
                <li>• How many public reports had the council received about this road before your incident?</li>
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* What AMBER overall means for claims */}
      <section className="py-16 bg-surface-50">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="font-display text-3xl font-bold text-brand-950 mb-4">
            Claiming Against A GREEN-Condition Council
          </h2>
          <p className="text-surface-600 mb-8">
            Honest assessment: Somerset's roads are better than most — here's how that changes your approach
          </p>

          <div className="grid md:grid-cols-2 gap-8 mb-8">
            <div className="bg-white rounded-2xl p-6 border border-surface-200">
              <h3 className="font-semibold text-brand-900 mb-4">What Works In The Council's Favour</h3>
              <ul className="text-surface-700 space-y-2 text-sm">
                <li className="flex items-start gap-2">
                  <span className="text-emerald-500">✓</span> GREEN condition scorecard — A roads at just 3% RED, network ahead of regional and national peers
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-emerald-500">✓</span> GREEN best practice — Band 3 (the top band) under the DfT's Incentive Fund, TAMP pioneer since 2009, 20 years of whole-asset management
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-emerald-500">✓</span> A documented Highway Safety Inspection Manual and scheduled safety inspections driving reactive repairs
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-emerald-500">✓</span> 89% of 2025/26 spend planned as preventative — 240km of treatments, 1.1m m² of surface dressing
                </li>
              </ul>
              <p className="text-surface-500 text-sm mt-4 italic">
                Expect a well-organised Section 58 defence with inspection records. Vague claims will struggle here.
              </p>
            </div>

            <div className="bg-rose-50 border border-rose-200 rounded-2xl p-6">
              <h3 className="font-semibold text-rose-900 mb-4">What Works In Yours</h3>
              <ul className="text-rose-800 space-y-2 text-sm">
                <li className="flex items-start gap-2">
                  <span className="text-rose-500">✗</span> RED spend scorecard — projecting £1m under its 2025/26 DfT allocation
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-rose-500">✗</span> Admits it cannot borrow to top up the budget due to a "financial emergency"
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-rose-500">✗</span> B/C roads in RED nearly doubled in two years (6% → 11%); green down from 67% to 59%
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-rose-500">✗</span> C and unclassified roads on four-year survey cycles — and U-road surveys can't record cracking
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-rose-500">✗</span> 112,648 potholes filled in five years; strategy openly deprioritises some known-poor roads
                </li>
              </ul>
            </div>
          </div>

          <div className="bg-brand-950 text-white rounded-2xl p-8">
            <h3 className="font-display text-xl font-bold mb-4">The Winning Strategy Here Is Specificity</h3>
            <p className="text-brand-200 mb-4">
              Against a council with a GREEN condition rating and genuine asset-management credentials, your 
              claim lives or dies on the <strong className="text-white">specific defect</strong>:
            </p>
            <ul className="text-brand-300 space-y-2 mb-6">
              <li>• Prior reports of the same pothole — Somerset's own reporting service and FixMyStreet are proof of actual notice</li>
              <li>• Photos showing size, depth and visible age — weathered edges and old patching defeat "it appeared overnight"</li>
              <li>• The road's class — on a C or U road, the four-year survey cycle is your strongest structural argument</li>
              <li>• The road's reporting history — if it was a known hotspot, the council's own top-10 ranking methodology proves it tracks exactly that</li>
            </ul>
            <p className="text-white font-medium">
              Mac builds exactly this case: he searches for prior reports, assesses your photo evidence, and 
              cites Somerset's own transparency data where it helps you.
            </p>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-16 bg-brand-950 text-white">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="font-display text-3xl font-bold mb-4">
            Hit a Pothole in Somerset?
          </h2>
          <p className="text-xl text-brand-200 mb-8">
            A well-run council demands a well-built claim. £35 for a professional claim pack.
          </p>

          <div className="grid sm:grid-cols-2 gap-6 mb-8">
            <div className="bg-brand-900/50 rounded-xl p-6 border border-brand-800">
              <h3 className="font-semibold text-brand-300 mb-3">DIY Claim</h3>
              <ul className="text-sm text-brand-400 space-y-1 text-left">
                <li>• Submit photos and invoices</li>
                <li>• Use generic template letter</li>
                <li>• No survey-cycle argument</li>
                <li>• No prior-report search</li>
                <li>• No RED-spend context</li>
              </ul>
            </div>
            <div className="bg-accent-500/20 rounded-xl p-6 border border-accent-500/30">
              <h3 className="font-semibold text-white mb-3">Professional Claim Pack</h3>
              <ul className="text-sm text-brand-200 space-y-1 text-left">
                <li>✅ B/C road decline documented</li>
                <li>✅ Four-year survey cycle argued</li>
                <li>✅ 112,648 potholes in five years cited</li>
                <li>✅ Prior reports searched and attached</li>
                <li>✅ Section 58 rebuttal tailored to Somerset</li>
              </ul>
            </div>
          </div>

          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link
              to="/check?council=somerset"
              className="inline-flex items-center justify-center px-8 py-4 bg-accent-500 text-white font-semibold rounded-xl hover:bg-accent-600 transition-all hover:shadow-lg hover:shadow-accent-500/25"
            >
              Order My Somerset Claim Pack — £35
              <svg className="ml-2 w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 8l4 4m0 0l-4 4m4-4H3" />
              </svg>
            </Link>
            <Link
              to="/road-maintenance-report"
              className="inline-flex items-center justify-center px-8 py-4 bg-white/10 text-white font-semibold rounded-xl hover:bg-white/20 transition-colors"
            >
              View All Council Ratings
            </Link>
          </div>

          <p className="text-brand-400 text-sm mt-6">
            No percentage fees. You keep 100% of any compensation.
          </p>
        </div>
      </section>

      {/* FAQs */}
      <section className="py-16 bg-surface-50">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="font-display text-2xl font-bold text-brand-950 mb-8">
            Frequently Asked Questions
          </h2>
          
          <div className="space-y-6">
            <div className="bg-white rounded-xl p-6 border border-surface-200">
              <h3 className="font-semibold text-brand-900 mb-2">
                Somerset's road condition is rated GREEN — can I still claim?
              </h3>
              <p className="text-surface-600 text-sm">
                Yes. The DfT's GREEN condition rating reflects network-wide averages, driven largely by 
                A roads that have stayed in good shape. Section 58 turns on the specific defect that damaged 
                your vehicle, not the average. Somerset's own data shows B and C roads in RED condition nearly 
                doubled from 6% in 2022 to 11% in 2024, good-condition B/C roads fell from 67% to 59%, and the 
                council still filled 24,483 potholes in 2024/25. Against a GREEN-rated council your claim needs 
                strong, specific evidence — photos, measurements and prior reports — but the defects clearly exist.
              </p>
            </div>

            <div className="bg-white rounded-xl p-6 border border-surface-200">
              <h3 className="font-semibold text-brand-900 mb-2">
                What does Somerset's RED spend scorecard mean for my claim?
              </h3>
              <p className="text-surface-600 text-sm">
                The DfT has flagged Somerset as underinvesting relative to its funding allocation. The council's 
                own transparency report projects £43.965m of capital spend for 2025/26 against a £44.965m DfT 
                allocation — £1m less than it was given. Its March 2024 funding report also admits it is 
                "currently unable" to supplement government grants with borrowing "due to the financial emergency 
                created by spiralling costs in adults and children's social care nationally." Your claim today 
                still turns on the specific defect, but a council cutting investment while its B and C roads 
                decline will find it progressively harder to show its maintenance system is reasonable.
              </p>
            </div>

            <div className="bg-white rounded-xl p-6 border border-surface-200">
              <h3 className="font-semibold text-brand-900 mb-2">
                What if my pothole was on a C road or an unclassified road?
              </h3>
              <p className="text-surface-600 text-sm">
                Coverage thins out fast below A and B roads. Somerset surveys 50% of its C roads each year, so 
                full coverage of the C network is only achieved every four years. Unclassified roads — 3,357.5km 
                of the county's 6,721.4km network — are surveyed at 25% per year using Multi-Function Road 
                Monitor vehicles, which the council notes record the same defects as SCANNER "except road 
                cracking." A defect on a minor rural road can sit between condition surveys for years, which 
                makes routine safety inspections and prior public reports the records that decide your claim.
              </p>
            </div>

            <div className="bg-white rounded-xl p-6 border border-surface-200">
              <h3 className="font-semibold text-brand-900 mb-2">
                Does Somerset's "Most Potholed Road programme" help my claim?
              </h3>
              <p className="text-surface-600 text-sm">
                Potentially, yes. For 2025/26 the council says "The top 10 roads in Somerset for reported 
                potholes will be resurfaced." That is documented, ranked knowledge of exactly where its worst 
                pothole hotspots are — built from report volumes. If your damage happened on a road with a heavy 
                pothole-reporting history, that history supports an argument the council had actual or 
                constructive notice of the road's condition before your incident.
              </p>
            </div>

            <div className="bg-white rounded-xl p-6 border border-surface-200">
              <h3 className="font-semibold text-brand-900 mb-2">
                Somerset filled 28,574 potholes in 2023/24 — what does that mean for claims?
              </h3>
              <p className="text-surface-600 text-sm">
                The council's own report calls that figure "Significantly above the usual trend for the highways 
                network in Somerset" and attributes it to the hot, dry summer of 2022 followed by a cold, wet 
                winter with flooding and sub-zero spells. That is roughly 78 potholes filled every day for a 
                year. A network producing defects that much faster than usual is exactly the environment where 
                potholes form between scheduled inspections — and where photographs, prior reports and dated 
                evidence of your specific defect carry the claim.
              </p>
            </div>

            <div className="bg-white rounded-xl p-6 border border-surface-200">
              <h3 className="font-semibold text-brand-900 mb-2">
                Who do I claim against for pothole damage in Somerset?
              </h3>
              <p className="text-surface-600 text-sm">
                Somerset Council. It is the unitary highway authority for the county's entire 6,721.4km local 
                road network, from the A roads linking Taunton, Bridgwater and Yeovil down to rural unclassified 
                lanes. Your claim is made under Section 41 of the Highways Act 1980, which places a duty on the 
                council to maintain the highway. Expect the council to defend under Section 58 by pointing to its 
                inspection regime — which is why your evidence needs to address when the road was last inspected 
                and what the council knew.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Attribution Footer */}
      <section className="py-8 bg-surface-100 border-t border-surface-200">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <p className="text-xs text-surface-500 text-center">
            Data sources: <a 
              href="https://www.gov.uk/government/publications/local-road-maintenance-ratings/local-road-maintenance-ratings-2025-to-2026"
              target="_blank"
              rel="noopener noreferrer"
              className="text-brand-600 hover:underline"
            >
              Department for Transport — Local Road Maintenance Ratings 2025 to 2026
            </a> | <a 
              href="https://www.somerset.gov.uk/roads-travel-and-parking/uk-government-funded-highways-repairs-and-maintenance/"
              target="_blank"
              rel="noopener noreferrer"
              className="text-brand-600 hover:underline"
            >
              Somerset Council Local Highways Maintenance Transparency Report (June 2025) and Network North Funding Report (March 2024)
            </a>. 
            Contains public sector information licensed under the{' '}
            <a href="https://www.nationalarchives.gov.uk/doc/open-government-licence/version/3/" target="_blank" rel="noopener noreferrer" className="text-brand-600 hover:underline">
              Open Government Licence v3.0
            </a>.
          </p>
        </div>
      </section>
    </div>
  );
}
